import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isHelpdeskMode } from '../../utils/helpdeskMode';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  FiberManualRecord as DotIcon,
  TrendingUp as TrendIcon,
  AccessTime as ClockIcon,
  PriorityHigh as UrgentIcon,
  Public as ExternalIcon,
  ConfirmationNumber as TicketIcon,
  CheckCircle as ResolvedIcon,
  HourglassTop as ProgressIcon,
  Inbox as OpenIcon,
} from '@mui/icons-material';
import { formatDistanceToNow, parseISO } from 'date-fns';
import api, { extractData } from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';

interface TicketStats {
  total: number;
  open: number;
  inProgress: number;
  resolved: number;
  closed: number;
  urgent: number;
  high: number;
  external: number;
  last24h: number;
  last7d: number;
}

interface Ticket {
  id: string;
  ticketNumber: string;
  subject: string;
  status: string;
  priority: string;
  category: string;
  requesterEmail: string | null;
  requesterName: string | null;
  userName?: string;
  userEmail?: string;
  assignedName?: string;
  assignedEmail?: string;
  createdAt: string;
  updatedAt: string;
}

interface TicketsResponse {
  tickets: Ticket[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const STATUS_COLORS: Record<string, string> = {
  open: '#3b82f6',
  in_progress: '#f59e0b',
  resolved: '#10b981',
  closed: '#6b7280',
};

const STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const PRIORITY_COLORS: Record<string, string> = {
  urgent: '#ef4444',
  high: '#f97316',
  medium: '#f59e0b',
  low: '#22c55e',
};

const HelpdeskDashboard: React.FC = () => {
  const navigate = useNavigate();
  const basePath = isHelpdeskMode() ? '/support' : '/helpdesk';
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  // API always uses /support, basePath is only for UI routing
  const ticketsPath = isAdmin ? '/support/tickets' : '/support/my-tickets';

  const [stats, setStats] = useState<TicketStats | null>(null);
  const [recentTickets, setRecentTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    setError('');
    try {
      const promises: Promise<any>[] = [
        api.get(ticketsPath, { params: { page: 1, limit: 10 } }),
      ];
      if (isAdmin) promises.push(api.get('/support/stats'));
      const results = await Promise.all(promises);
      const tData = extractData<TicketsResponse>(results[0]);
      setRecentTickets(tData.tickets || []);

      if (isAdmin && results[1]) {
        setStats(extractData<TicketStats>(results[1]));
      } else {
        // Build basic stats from tickets for non-admin
        const tickets: Ticket[] = tData.tickets || [];
        setStats({
          total: tData.total || tickets.length,
          open: tickets.filter((t: Ticket) => t.status === 'open').length,
          inProgress: tickets.filter((t: Ticket) => t.status === 'in_progress').length,
          resolved: tickets.filter((t: Ticket) => t.status === 'resolved').length,
          closed: tickets.filter((t: Ticket) => t.status === 'closed').length,
          urgent: tickets.filter((t: Ticket) => t.priority === 'urgent').length,
          high: tickets.filter((t: Ticket) => t.priority === 'high').length,
          external: 0,
          last24h: 0,
          last7d: 0,
        });
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  const statCards = [
    { label: 'Open', value: stats?.open || 0, icon: <OpenIcon />, color: '#3b82f6', bg: '#eff6ff' },
    { label: 'In Progress', value: stats?.inProgress || 0, icon: <ProgressIcon />, color: '#f59e0b', bg: '#fffbeb' },
    { label: 'Resolved', value: stats?.resolved || 0, icon: <ResolvedIcon />, color: '#10b981', bg: '#ecfdf5' },
    { label: 'Total Tickets', value: stats?.total || 0, icon: <TicketIcon />, color: '#6366f1', bg: '#eef2ff' },
  ];

  const adminCards = isAdmin ? [
    { label: 'Last 24h', value: stats?.last24h || 0, icon: <ClockIcon />, color: '#8b5cf6', bg: '#f5f3ff' },
    { label: 'Urgent', value: stats?.urgent || 0, icon: <UrgentIcon />, color: '#ef4444', bg: '#fef2f2' },
    { label: 'High Priority', value: stats?.high || 0, icon: <TrendIcon />, color: '#f97316', bg: '#fff7ed' },
    { label: 'External', value: stats?.external || 0, icon: <ExternalIcon />, color: '#06b6d4', bg: '#ecfeff' },
  ] : [];

  // Priority distribution for chart
  const priorityDist = [
    { label: 'Urgent', count: stats?.urgent || 0, color: PRIORITY_COLORS.urgent },
    { label: 'High', count: stats?.high || 0, color: PRIORITY_COLORS.high },
    { label: 'Medium', count: Math.max((stats?.total || 0) - (stats?.urgent || 0) - (stats?.high || 0) - (stats?.closed || 0), 0), color: PRIORITY_COLORS.medium },
    { label: 'Low', count: stats?.closed || 0, color: PRIORITY_COLORS.low },
  ];
  const totalPriority = priorityDist.reduce((s, p) => s + p.count, 0) || 1;

  // Status distribution
  const statusDist = [
    { label: 'Open', count: stats?.open || 0, color: STATUS_COLORS.open },
    { label: 'In Progress', count: stats?.inProgress || 0, color: STATUS_COLORS.in_progress },
    { label: 'Resolved', count: stats?.resolved || 0, color: STATUS_COLORS.resolved },
    { label: 'Closed', count: stats?.closed || 0, color: STATUS_COLORS.closed },
  ];
  const totalStatus = statusDist.reduce((s, st) => s + st.count, 0) || 1;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Page Title */}
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: '#1f2937' }}>
        {isAdmin ? 'Helpdesk Dashboard' : 'My Support Dashboard'}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Primary Stats */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
        {statCards.map((card) => (
          <Paper
            key={card.label}
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              cursor: 'pointer',
              transition: 'all 0.15s',
              '&:hover': { borderColor: card.color, boxShadow: `0 2px 12px ${card.color}15` },
            }}
            onClick={() => navigate(`${basePath}/tickets`)}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                bgcolor: card.bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: card.color,
                '& .MuiSvgIcon-root': { fontSize: 24 },
              }}
            >
              {card.icon}
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#1f2937', lineHeight: 1.2 }}>
                {card.value}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                {card.label}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Admin Extra Stats */}
      {adminCards.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
          {adminCards.map((card) => (
            <Paper
              key={card.label}
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  bgcolor: card.bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: card.color,
                  '& .MuiSvgIcon-root': { fontSize: 20 },
                }}
              >
                {card.icon}
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#1f2937', lineHeight: 1.2 }}>
                  {card.value}
                </Typography>
                <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 500 }}>
                  {card.label}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* Charts Row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
        {/* Tickets by Status */}
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: '#1f2937' }}>
            Tickets by Status
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            {/* Donut-like visual */}
            <Box sx={{ position: 'relative', width: 120, height: 120, flexShrink: 0 }}>
              <svg viewBox="0 0 36 36" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                {(() => {
                  let offset = 0;
                  return statusDist.map((s, i) => {
                    const pct = (s.count / totalStatus) * 100;
                    const el = (
                      <circle
                        key={i}
                        cx="18" cy="18" r="15.9"
                        fill="none"
                        stroke={s.color}
                        strokeWidth="3.5"
                        strokeDasharray={`${pct} ${100 - pct}`}
                        strokeDashoffset={-offset}
                      />
                    );
                    offset += pct;
                    return el;
                  });
                })()}
              </svg>
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1 }}>{stats?.total || 0}</Typography>
                <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>Total</Typography>
              </Box>
            </Box>
            {/* Legend */}
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1 }}>
              {statusDist.map((s) => (
                <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DotIcon sx={{ fontSize: 10, color: s.color }} />
                  <Typography variant="body2" sx={{ color: '#4b5563', minWidth: 80 }}>{s.label}</Typography>
                  <Typography variant="body2" fontWeight={600}>{s.count}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>

        {/* Tickets by Priority */}
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: '#1f2937' }}>
            Tickets by Priority
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {priorityDist.map((p) => (
              <Box key={p.label}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ color: '#4b5563' }}>{p.label}</Typography>
                  <Typography variant="body2" fontWeight={600}>{p.count}</Typography>
                </Box>
                <Box sx={{ height: 8, bgcolor: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      height: '100%',
                      width: `${(p.count / totalPriority) * 100}%`,
                      bgcolor: p.color,
                      borderRadius: 4,
                      transition: 'width 0.5s ease',
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>

      {/* Recent Tickets */}
      <Paper elevation={0} sx={{ borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2.5, pb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#1f2937' }}>
            Recent Tickets
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: '#3b82f6', cursor: 'pointer', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
            onClick={() => navigate(`${basePath}/tickets`)}
          >
            View All →
          </Typography>
        </Box>
        {/* Table Header */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '100px 1fr 100px 100px 140px 120px',
            px: 2.5,
            py: 1,
            bgcolor: '#f9fafb',
            borderTop: '1px solid #e5e7eb',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          {['Ticket #', 'Subject', 'Status', 'Priority', 'Requester', 'Created'].map((h) => (
            <Typography key={h} variant="caption" fontWeight={600} sx={{ color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {h}
            </Typography>
          ))}
        </Box>
        {/* Rows */}
        {recentTickets.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No tickets yet</Typography>
          </Box>
        ) : (
          recentTickets.slice(0, 8).map((t) => (
            <Box
              key={t.id}
              onClick={() => navigate(`${basePath}/tickets/${t.id}`)}
              sx={{
                display: 'grid',
                gridTemplateColumns: '100px 1fr 100px 100px 140px 120px',
                px: 2.5,
                py: 1.5,
                cursor: 'pointer',
                borderBottom: '1px solid #f3f4f6',
                alignItems: 'center',
                transition: 'background 0.1s',
                '&:hover': { bgcolor: '#f9fafb' },
              }}
            >
              <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#6366f1', fontWeight: 600, fontSize: '0.8rem' }}>
                {t.ticketNumber}
              </Typography>
              <Typography variant="body2" sx={{ color: '#1f2937', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pr: 2 }}>
                {t.subject}
              </Typography>
              <Chip
                size="small"
                label={STATUS_LABELS[t.status] || t.status}
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  bgcolor: `${STATUS_COLORS[t.status] || '#9e9e9e'}15`,
                  color: STATUS_COLORS[t.status] || '#9e9e9e',
                  width: 'fit-content',
                }}
              />
              <Chip
                size="small"
                label={t.priority?.charAt(0).toUpperCase() + t.priority?.slice(1)}
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  bgcolor: `${PRIORITY_COLORS[t.priority] || '#9e9e9e'}12`,
                  color: PRIORITY_COLORS[t.priority] || '#9e9e9e',
                  width: 'fit-content',
                }}
              />
              <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t.requesterName || t.userName || t.requesterEmail || t.userEmail || '—'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                {formatDistanceToNow(parseISO(t.createdAt), { addSuffix: true })}
              </Typography>
            </Box>
          ))
        )}
      </Paper>
    </Box>
  );
};

export default HelpdeskDashboard;
